import { useMemo } from 'react'
import { Edit } from 'lucide-react'
import { Button } from '@/components/ui/button'
import PageView from './PageView'
import { splitContentByPageBreaks, getPageStatistics } from './utils/pageSplitter'
import './PagedEditor.css'

/**
 * PagedEditor Component (Refactored)
 *
 * PREVIEW-ONLY component that displays document split into multiple pages.
 * NO INTERNAL EDITOR - receives content from parent and displays it read-only.
 *
 * This is NOT an editor, it's a preview/display component.
 * For editing, user must switch back to standard mode.
 */
export default function PagedEditor({
  content,
  pageFormat = 'A4',
  showPageNumbers = true,
  header = null,
  footer = null,
  headerHeight = 60,
  footerHeight = 60,
  onEditClick = null,
}) {
  // Split content into pages based on pageBreak nodes
  const pages = useMemo(() => {
    return splitContentByPageBreaks(content)
  }, [content])

  // Get page statistics
  const stats = useMemo(() => {
    return getPageStatistics(content)
  }, [content])

  // Page format info
  const pageFormats = {
    A4: {
      name: 'A4 (210 x 297 mm)',
    },
    Letter: {
      name: 'Letter (8.5 x 11 in)',
    },
  }

  const formatInfo = pageFormats[pageFormat] || pageFormats.A4

  return (
    <div className="paged-editor-container">
      {/* Edit Mode Button */}
      {onEditClick && (
        <div className="paged-editor-actions">
          <Button
            onClick={onEditClick}
            className="gap-1"
          >
            <Edit className="h-4 w-4" />
            Edit Document
          </Button>
        </div>
      )}

      {/* Pages Container */}
      <div className="paged-editor-pages">
        {pages.map((pageContent, index) => (
          <PageView
            key={index}
            content={pageContent}
            pageNumber={index + 1}
            totalPages={pages.length}
            header={header}
            footer={footer}
            headerHeight={headerHeight}
            footerHeight={footerHeight}
            pageFormat={pageFormat}
            showPageNumbers={showPageNumbers}
            onDoubleClick={onEditClick}
          />
        ))}
      </div>

      {/* Page Info Footer */}
      <div className="paged-editor-info">
        <div className="flex items-center justify-center gap-6">
          <span className="text-sm text-slate-500">
            {formatInfo.name}
          </span>
          <span className="text-sm text-slate-500">
            {stats.totalPages} {stats.totalPages === 1 ? 'page' : 'pages'}
          </span>
          {stats.pageBreaks > 0 && (
            <span className="text-sm text-slate-500">
              {stats.pageBreaks} page {stats.pageBreaks === 1 ? 'break' : 'breaks'}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
